<template>
  <HeaderTop access-denied-mode />
  <div class="access-denied">
    <div class="access-denied__card">
      <div class="access-denied__content">
        <h1 class="access-denied__title">
          {{ t("accessDenied.title") }}
        </h1>
        <p class="access-denied__message">
          {{ message }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import HeaderTop from "@/components/HeaderTop.vue";
import habService from "@/services/habService";
import KeycloakService from "@/services/keycloakService";
import { UseStoreUser } from "@/store/userStore";
import { hasHubRoleFromPayload, parseAccessTokenPayload } from "@/utils/token";
import type { AccessTokenPayload } from "@/utils/token";

const props = defineProps({
  message: {
    type: String,
    default: undefined
  }
});

const { t } = useI18n();
const router = useRouter();

const message = props.message && t(props.message) || t("accessDenied.productAccessMessage");

function userHasHubAccess(): boolean {
  const userStore = UseStoreUser();
  if (userStore.hasAnyHubRole) {
    return true;
  }
  const tokenParsed = KeycloakService.currentInstance?.tokenParsed;
  if (
    tokenParsed &&
    typeof tokenParsed === "object" &&
    hasHubRoleFromPayload(tokenParsed as AccessTokenPayload)
  ) {
    return true;
  }
  const raw =
    typeof KeycloakService.token === "function"
      ? KeycloakService.token()
      : undefined;
  const decoded = raw?.trim()
    ? parseAccessTokenPayload(raw)
    : null;
  return decoded !== null && hasHubRoleFromPayload(decoded);
}

onMounted(async () => {
  habService.loadUserRolesFromToken();
  await nextTick();
  if (userHasHubAccess()) {
    await router.replace({ name: "home" });
  }
});
</script>

<style scoped>
.access-denied {
  min-height: 100vh;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 1rem;
  padding-right: 1rem;
}

.access-denied__card {
  max-width: 28rem;
  width: 100%;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  padding: 2rem;
}

.access-denied__content {
  text-align: center;
}

.access-denied__title {
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
}

.access-denied__message {
  color: #4b5563;
  margin-bottom: 1.5rem;
}
</style>
